import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getJob, getJobApplications, updateApplicationStatus } from '../../services/jobs';
import type { Job, JobApplication, ApplicationStatus } from '../../types/job';
import { APPLICATION_STATUS_LABELS } from '../../types/job';

const STATUS_COLORS: Record<ApplicationStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  viewed: 'bg-blue-100 text-blue-800',
  reviewing: 'bg-blue-100 text-blue-800',
  shortlisted: 'bg-green-100 text-green-800',
  interviewing: 'bg-purple-100 text-purple-800',
  offered: 'bg-green-200 text-green-900',
  accepted: 'bg-green-300 text-green-900',
  rejected: 'bg-red-100 text-red-800',
  withdrawn: 'bg-gray-100 text-gray-600',
};

const STATUS_ACTIONS: Record<ApplicationStatus, ApplicationStatus[]> = {
  pending: ['viewed', 'reviewing', 'rejected'],
  viewed: ['reviewing', 'shortlisted', 'rejected'],
  reviewing: ['shortlisted', 'rejected'],
  shortlisted: ['interviewing', 'rejected'],
  interviewing: ['offered', 'rejected'],
  offered: ['accepted', 'rejected'],
  accepted: [],
  rejected: [],
  withdrawn: [],
};

export function JobApplicationsPage() {
  const { id } = useParams<{ id: string }>();

  const [job, setJob] = useState<Job | null>(null);
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [filteredApplications, setFilteredApplications] = useState<JobApplication[]>([]);
  const [statusFilter, setStatusFilter] = useState<ApplicationStatus | 'all'>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [selectedApplication, setSelectedApplication] = useState<JobApplication | null>(null);

  useEffect(() => {
    if (id) {
      loadData(id);
    }
  }, [id]);

  useEffect(() => {
    if (statusFilter === 'all') {
      setFilteredApplications(applications);
    } else {
      setFilteredApplications(applications.filter(app => app.status === statusFilter));
    }
  }, [applications, statusFilter]);

  const loadData = async (jobId: string) => {
    setLoading(true);
    setError(null);

    try {
      const [jobData, appData] = await Promise.all([
        getJob(jobId),
        getJobApplications(jobId),
      ]);
      setJob(jobData);
      setApplications(appData);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load applications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (applicationId: string, newStatus: ApplicationStatus) => {
    setUpdatingId(applicationId);

    try {
      await updateApplicationStatus(applicationId, { status: newStatus });
      setApplications(prev =>
        prev.map(app =>
          app.id === applicationId ? { ...app, status: newStatus } : app
        )
      );
      if (selectedApplication?.id === applicationId) {
        setSelectedApplication(prev => prev ? { ...prev, status: newStatus } : null);
      }
    } catch (err: any) {
      console.error('Failed to update status:', err);
      alert(err.response?.data?.error || 'Failed to update application status');
    } finally {
      setUpdatingId(null);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-IE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusCounts = () => {
    const counts: Record<string, number> = { all: applications.length };
    applications.forEach(app => {
      counts[app.status] = (counts[app.status] || 0) + 1;
    });
    return counts;
  };

  const statusCounts = getStatusCounts();

  if (loading) {
    return (
      <div className="py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-64 mb-2" />
            <div className="h-4 bg-gray-200 rounded w-48 mb-8" />
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg shadow-sm p-6">
                  <div className="h-5 bg-gray-200 rounded w-1/3 mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-1/4" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !job) {
    return (
      <div className="py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{error}</h2>
            <Link to="/company/jobs" className="text-blue-600 hover:text-blue-800">
              Back to Jobs
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/company/jobs"
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Jobs
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Applications for {job?.title}</h1>
          <p className="text-gray-600 mt-1">{applications.length} total applications</p>
        </div>

        {/* Status filter */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex gap-4 -mb-px overflow-x-auto">
            {(['all', 'pending', 'reviewing', 'shortlisted', 'interviewing', 'offered', 'accepted', 'rejected'] as const).map(status => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`py-3 px-1 text-sm font-medium border-b-2 whitespace-nowrap ${
                  statusFilter === status
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {status === 'all' ? 'All' : APPLICATION_STATUS_LABELS[status]}
                {statusCounts[status] ? (
                  <span className="ml-1 text-xs text-gray-400">({statusCounts[status]})</span>
                ) : null}
              </button>
            ))}
          </nav>
        </div>

        <div className="flex gap-6">
          {/* Applications list */}
          <div className="flex-1">
            {filteredApplications.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">No applications found</h3>
                <p className="text-gray-600">
                  {statusFilter === 'all'
                    ? 'No one has applied to this job yet.'
                    : `No applications with status "${APPLICATION_STATUS_LABELS[statusFilter as ApplicationStatus]}".`}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredApplications.map(application => (
                  <div
                    key={application.id}
                    onClick={() => setSelectedApplication(application)}
                    className={`bg-white rounded-lg shadow-sm border p-4 cursor-pointer transition-colors ${
                      selectedApplication?.id === application.id
                        ? 'border-blue-300 ring-1 ring-blue-100'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {application.expert?.avatar_url ? (
                          <img
                            src={application.expert.avatar_url}
                            alt={`${application.expert.first_name} ${application.expert.last_name}`}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-medium">
                            {application.expert?.first_name?.charAt(0) || 'E'}
                          </div>
                        )}
                        <div>
                          <h3 className="text-sm font-medium text-gray-900">
                            {application.expert?.first_name} {application.expert?.last_name}
                          </h3>
                          <p className="text-xs text-gray-500">
                            {application.expert?.title || 'Expert'}
                            {application.expert?.location && ` · ${application.expert.location}`}
                          </p>
                        </div>
                      </div>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[application.status]}`}
                      >
                        {APPLICATION_STATUS_LABELS[application.status]}
                      </span>
                    </div>
                    <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
                      <span>Applied: {formatDate(application.created_at)}</span>
                      {application.unread_count && application.unread_count > 0 && (
                        <span className="text-blue-600 font-medium">
                          {application.unread_count} new messages
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Selected application detail */}
          {selectedApplication && (
            <div className="w-96 bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-4 h-fit">
              <div className="flex items-center gap-4 mb-6">
                {selectedApplication.expert?.avatar_url ? (
                  <img
                    src={selectedApplication.expert.avatar_url}
                    alt={`${selectedApplication.expert.first_name} ${selectedApplication.expert.last_name}`}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold text-xl">
                    {selectedApplication.expert?.first_name?.charAt(0) || 'E'}
                  </div>
                )}
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {selectedApplication.expert?.first_name} {selectedApplication.expert?.last_name}
                  </h2>
                  <p className="text-sm text-gray-600">{selectedApplication.expert?.title}</p>
                </div>
              </div>

              {/* Status */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${STATUS_COLORS[selectedApplication.status]}`}
                >
                  {APPLICATION_STATUS_LABELS[selectedApplication.status]}
                </span>
              </div>

              {/* Status actions */}
              {STATUS_ACTIONS[selectedApplication.status].length > 0 && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Update Status</label>
                  <div className="flex flex-wrap gap-2">
                    {STATUS_ACTIONS[selectedApplication.status].map(status => (
                      <button
                        key={status}
                        onClick={() => handleStatusUpdate(selectedApplication.id, status)}
                        disabled={updatingId === selectedApplication.id}
                        className={`px-3 py-1 text-sm rounded-md border ${
                          status === 'rejected'
                            ? 'border-red-300 text-red-700 hover:bg-red-50'
                            : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                        } disabled:opacity-50`}
                      >
                        {APPLICATION_STATUS_LABELS[status]}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Cover letter */}
              {selectedApplication.cover_letter && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Cover Letter</label>
                  <div className="text-sm text-gray-600 bg-gray-50 rounded-md p-3 max-h-40 overflow-y-auto">
                    {selectedApplication.cover_letter}
                  </div>
                </div>
              )}

              {/* Links */}
              <div className="space-y-2 mb-6">
                {selectedApplication.resume_url && (
                  <a
                    href={selectedApplication.resume_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    View Resume
                  </a>
                )}
                {selectedApplication.portfolio_url && (
                  <a
                    href={selectedApplication.portfolio_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    View Portfolio
                  </a>
                )}
              </div>

              {/* Availability */}
              {selectedApplication.availability && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Availability</label>
                  <p className="text-sm text-gray-600">{selectedApplication.availability}</p>
                </div>
              )}

              {/* Proposed rate */}
              {selectedApplication.proposed_rate && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Proposed Rate</label>
                  <p className="text-sm text-gray-600">€{selectedApplication.proposed_rate}/day</p>
                </div>
              )}

              {/* Actions */}
              <div className="pt-4 border-t border-gray-200">
                <Link
                  to={`/company/applications/${selectedApplication.id}/messages`}
                  className="block w-full text-center px-4 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700"
                >
                  Send Message
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
